<template>
  <div>
    <transition
      name="custom-classes-transition"
    >
      <div v-if="isVisible" class="song-select-all-bar" :class="{ 'is-processing': isProcessing }">
        <div class="song-select-all-bar-content">
          <!-- Left side: Close button and count -->
          <div class="song-select-all-bar-left">
            <button class="song-select-all-bar-close" @click="close" :disabled="isProcessing">
              <IconX />
            </button>
            <span class="song-select-all-bar-count">{{ selectedCount }} songs selected</span>
          </div>

          <!-- Separator -->
          <div class="song-select-all-bar-separator"></div>

          <!-- Right side: Action buttons -->
          <div class="song-select-all-bar-actions">
            <button class="song-select-all-bar-action" @click="downloadLyrics" :disabled="isProcessing">
              <IconFileText />
              <span>{{ isDownloadingLyrics ? 'Downloading...' : 'Download lyrics' }}</span>
            </button>
            <button class="song-select-all-bar-action" @click="downloadAudio" :disabled="isProcessing">
              <IconDownload />
              <span>{{ isDownloadingAudio ? 'Downloading...' : 'Download audio' }}</span>
            </button>
            <button class="song-select-all-bar-action" @click="addToQueue" :disabled="isProcessing">
              <IconPlus />
              <span>{{ isAddingToQueue ? 'Adding...' : 'Add to queue' }}</span>
            </button>
            <button class="song-select-all-bar-action" @click="addToPlaylist" :disabled="isProcessing">
              <IconPlaylist />
              <span>Add to playlist</span>
            </button>
            <button class="song-select-all-bar-action" @click="addNote" :disabled="isProcessing">
              <IconFileText />
              <span>Add note</span>
            </button>
            <button class="song-select-all-bar-action song-select-all-bar-action-danger" @click="deleteSelected" :disabled="isProcessing">
              <IconTrash />
              <span>{{ isDeleting ? 'Deleting...' : 'Delete' }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <NoteModal
      :is-visible="showNoteModal"
      :selected-songs="selectedSongs"
      :selected-count="selectedCount"
      @close="closeNoteModal"
      @notes-saved="handleNotesSaved"
    />

    <PlaylistSelectModal
      :is-visible="showPlaylistModal"
      :selected-songs="selectedSongs"
      :selected-audio-file-ids="selectedAudioFileIds"
      :selected-count="selectedCount"
      @close="closePlaylistModal"
      @playlist-added="handlePlaylistAdded"
    />
  </div>
</template>

<script>
import { IconX, IconTrash, IconFileText, IconDownload, IconPlus, IconPlaylist } from '@/Icons/IconBase'
import { DOWNLOAD_CREATE, QUEUED_AUDIO_FILES_USER_CREATE } from '@/store/actions'
import NoteModal from '@/components/Modals/NoteModal'
import PlaylistSelectModal from '@/components/Modals/PlaylistSelectModal'

export default {
  name: 'SelectionBar',
  components: {
    IconX,
    IconTrash,
    IconFileText,
    IconDownload,
    IconPlus,
    IconPlaylist,
    NoteModal,
    PlaylistSelectModal
  },
  props: {
    selectedCount: {
      type: Number,
      default: 0
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    selectedSongs: {
      type: Array,
      default: () => []
    },
    selectedAudioFileIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isDeleting: false,
      isDownloadingLyrics: false,
      isDownloadingAudio: false,
      isAddingToQueue: false,
      showNoteModal: false,
      showPlaylistModal: false,
      pendingAction: null // Track the current pending action
    }
  },
  computed: {
    isProcessing() {
      return this.isDeleting || this.isDownloadingLyrics || this.isDownloadingAudio || this.isAddingToQueue
    }
  },
  methods: {
    refresh() {
      this.$nextTick(() => {
        console.log('Emitting refresh-songs event')
        this.$emit('refresh-songs')
        console.log('Emitting clear-selection event')
        this.$emit('clear-selection')
      })
    },
    close() {
      this.$emit('close')
    },
    closeAllModals() {
      this.showNoteModal = false
      this.showPlaylistModal = false
      // Clear any open global confirm modal
      this.$store.commit('CONFIRM_CLEAR')
      // Cancel any pending action
      this.pendingAction = null
    },
    downloadLyrics() {
      this.closeAllModals()
      this.pendingAction = 'downloadLyrics'
      this.confirmDownload('lyric_files', 'lyrics')
    },
    downloadAudio() {
      this.closeAllModals()
      this.pendingAction = 'downloadAudio'
      this.confirmDownload('audio_files', 'audio files')
    },
    confirmDownload(type, displayType) {
      const count = type === 'lyric_files' ? this.selectedSongs.filter(song => song.lyrics_id).length : this.selectedAudioFileIds.length

      if (type === 'lyric_files') {
        console.log('selectedSongs', this.selectedSongs)
        const hasLyrics = this.selectedSongs.some(song => song.lyrics_id)
        if (!hasLyrics) {
          alert('There are no lyrics to download for the selected songs.')
          this.pendingAction = null
          return
        }
      } else if (type === 'audio_files') {
        if (this.selectedAudioFileIds.length === 0) {
          alert('There are no audio files to download for the selected songs.')
          this.pendingAction = null
          return
        }
      }

      const message = type === 'lyric_files' ? `Are you sure you want to download ${count} ${displayType} from the ${this.selectedSongs.length} selected songs?` : `Are you sure you want to download ${count} ${displayType} from the ${this.selectedSongs.length} selected songs?`

      this.$store.dispatch('confirm', message).then(resp => {
        // Only proceed if this is still the current pending action
        if (this.pendingAction === (type === 'lyric_files' ? 'downloadLyrics' : 'downloadAudio') && resp === true) {
          this.createDownload(type)
        }
        this.pendingAction = null
      }).catch(err => {
        console.log(err)
        this.pendingAction = null
      })
    },
    createDownload(type) {
      if (type === 'lyric_files' && this.selectedSongs.length === 0) {
        this.$store.commit('error', 'No songs selected for lyrics download.')
        return
      }

      if (type === 'audio_files' && this.selectedAudioFileIds.length === 0) {
        this.$store.commit('error', 'No audio files selected for download.')
        return
      }

      // Set loading state
      if (type === 'lyric_files') {
        this.isDownloadingLyrics = true
      } else {
        this.isDownloadingAudio = true
      }

      const downloadData = {
        type: type,
        title: `${type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} `,
        parameters: type === 'lyric_files'
          ? { song_ids: this.selectedSongs.map(song => song.id) }
          : { audio_file_ids: this.selectedAudioFileIds }
      }

      this.$store.dispatch(DOWNLOAD_CREATE, downloadData)
        .then(() => {
          this.$store.commit('download', `Your ${type.replace('_', ' ')} download is in process.`)
          this.close()
          this.refresh()
        })
        .catch(err => {
          if (err.response) {
            this.$store.commit('error', err.response.data.message)
          } else {
            this.$store.commit('error', 'Download could not be processed.')
          }
        })
        .finally(() => {
          if (type === 'lyric_files') {
            this.isDownloadingLyrics = false
          } else {
            this.isDownloadingAudio = false
          }
        })
    },
    addToQueue() {
      this.closeAllModals()
      this.pendingAction = 'addToQueue'
      const count = this.selectedAudioFileIds.length
      const message = `Are you sure you want to add ${count} audio file(s) to your queue?`

      this.$store.dispatch('confirm', message).then(resp => {
        if (this.pendingAction === 'addToQueue' && resp === true) {
          this.addToQueueConfirmed()
        }
        this.pendingAction = null
      }).catch(err => {
        console.log(err)
        this.pendingAction = null
      })
    },

    addToQueueConfirmed() {
      this.isAddingToQueue = true
      this.$store.dispatch(QUEUED_AUDIO_FILES_USER_CREATE, {
        audio_file_ids: this.selectedAudioFileIds
      })
        .then(() => {
          this.$store.commit('alert', `${this.selectedAudioFileIds.length} audio file(s) added to your queue.`)
          this.close()
          this.refresh()
        })
        .catch(err => {
          console.log('err', err)
          if (err.response) {
            this.$store.commit('error', err.response.data.message)
          } else {
            this.$store.commit('error', 'Could not add to queue.')
          }
        })
        .finally(() => {
          this.isAddingToQueue = false
        })
    },
    addToPlaylist() {
      this.closeAllModals()
      this.showPlaylistModal = true
    },
    closePlaylistModal() {
      this.showPlaylistModal = false
    },
    handlePlaylistAdded() {
      this.closePlaylistModal()
      this.refresh()
    },
    addNote() {
      this.closeAllModals()
      this.showNoteModal = true
    },
    closeNoteModal() {
      this.showNoteModal = false
    },
    handleNotesSaved() {
      this.closeNoteModal()
      this.refresh()
    },
    deleteSelected() {
      this.closeAllModals()
      this.pendingAction = 'deleteSelected'
      const count = this.selectedSongs.length
      const message = `Are you sure you want to delete ${count} song(s)? This action cannot be undone.`

      this.$store.dispatch('confirm', message).then(resp => {
        // Only proceed if this is still the current pending action
        if (this.pendingAction === 'deleteSelected' && resp === true) {
          this.bulkDeleteSongs()
        }
        this.pendingAction = null
      }).catch(err => {
        console.log(err)
        this.pendingAction = null
      })
    },

    bulkDeleteSongs() {
      const songIds = this.selectedSongs.map(song => song.id)
      console.log('Starting bulk delete for song IDs:', songIds)
      this.isDeleting = true

      this.$store.dispatch('SONG_BULK_DELETE', songIds)
        .then(() => {
          console.log('Bulk delete successful, emitting events')
          this.$store.commit('alert', `${songIds.length} song(s) deleted`)

          // Close the selection bar first
          this.close()

          // Don't reload immediately - rely on optimistic UI update
          // The songs are already removed from the UI via SONG_BULK_DELETED mutation
          // Elasticsearch will be updated in the background via queued jobs
          // The list will refresh naturally when user navigates or manually refreshes
        })
        .catch(err => {
          console.error('Bulk delete failed:', err)
          if (err.response) {
            this.$store.commit('error', err.response.data.message)
          } else {
            this.$store.commit('error', 'Songs could not be deleted.')
          }
        })
        .finally(() => {
          this.isDeleting = false
        })
    }
  }
}
</script>

<!-- <style scoped>
.song-select-all-bar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background-color: #4a4a4a;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 500px;
  max-width: 90vw;
}

.song-select-all-bar-content {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: white;
}

.song-select-all-bar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.song-select-all-bar-close {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.song-select-all-bar-close:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.song-select-all-bar-close svg {
  width: 16px;
  height: 16px;
  stroke: white;
}

.song-select-all-bar-count {
  font-size: 14px;
  font-weight: 500;
  color: white;
}

.song-select-all-bar-separator {
  width: 1px;
  height: 20px;
  background-color: rgba(255, 255, 255, 0.2);
  margin: 0 16px;
}

.song-select-all-bar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.song-select-all-bar-action {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 14px;
  transition: background-color 0.2s ease;
  white-space: nowrap;
}

.song-select-all-bar-action:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.song-select-all-bar-action svg {
  width: 16px;
  height: 16px;
  stroke: white;
}

.song-select-all-bar-action-danger {
  color: #ff6b35;
}

.song-select-all-bar-action-danger svg {
  stroke: #ff6b35;
}

.song-select-all-bar-action-danger:hover {
  background-color: rgba(255, 107, 53, 0.1);
}

/* Responsive adjustments */
@media (max-width: 980px) {
  .song-select-all-bar {
    left: 10px;
    right: 10px;
    transform: none;
    min-width: auto;
  }

  .song-select-all-bar-actions {
    gap: 8px;
  }

  .song-select-all-bar-action {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 10px;
  transition: background-color 0.2s ease;
  white-space: nowrap;
}
@media (max-width: 760px) {
  .song-select-all-bar {
    left: 10px;
    right: 10px;
    transform: none;
    min-width: auto;
  }
.song-select-all-bar-action-danger {
  color: #ff6b35;
}

.song-select-all-bar-action-danger svg {
  stroke: #ff6b35;
}

.song-select-all-bar-action-danger:hover {
  background-color: rgba(255, 107, 53, 0.1);
}

  .song-select-all-bar-actions {
    gap: 24px;
  }

  .song-select-all-bar-action span {    display: none;
  }
}
}
</style> -->
